import { useCallback, useEffect, useMemo, useState } from 'react'
import { actionPlansServices } from '@/service/actionPlans'
import { areaService } from '@/service/area'
import { departmentService } from '@/service/department'
import { listRoles } from '@/service/roles'
import { httpFetch } from '@/utils/crud'
import { Roles } from '@/utils/enums/roles'
import { Item } from '@/utils/types/generals'
import {
  getAvailableUsersForActionPlan,
  getStoresForFilter,
} from '../../createActionPlan/methods'
import { AvailableUser } from '../../createActionPlan/types'

export interface AssignedUserFilters {
  stores?: number[]
  userTypes?: number[]
  roles?: number[]
}

export interface ApproverFilters {
  areas?: number[]
  departments?: number[]
  userTypes?: number[]
}

export interface FilterOptions {
  stores: Item[]
  areas: Item[]
  departments: Item[]
  assignedUserTypes: Item[]
  assignedRoles: Item[]
}

const getFullName = (firstName?: string, lastName?: string) =>
  `${firstName ?? ''} ${lastName ?? ''}`.trim()

const mapToAvailableUser = (user: {
  id: number
  firstName?: string
  lastName?: string
  roleId?: number
  role?: string | { description?: string }
  userTypeId?: number
  userType?: string | { description?: string }
  email?: string
  picture?: string | null
}): AvailableUser => ({
  id: user.id,
  firstName: user.firstName ?? '',
  lastName: user.lastName ?? '',
  fullName: getFullName(user.firstName, user.lastName),
  roleId: user.roleId ?? 0,
  role:
    typeof user.role === 'string'
      ? user.role
      : user.role?.description ?? '',
  userTypeId: user.userTypeId ?? 0,
  userType:
    typeof user.userType === 'string'
      ? user.userType
      : user.userType?.description ?? '',
  email: user.email ?? '',
  picture: user.picture ?? null,
})

export const useActionPlanUsers = () => {
  const [assignedUsers, setAssignedUsers] = useState<AvailableUser[]>([])
  const [approverUsers, setApproverUsers] = useState<AvailableUser[]>([])
  const [isLoadingAssigned, setIsLoadingAssigned] = useState(false)
  const [isLoadingApprovers, setIsLoadingApprovers] = useState(false)
  const [assignedError, setAssignedError] = useState(false)
  const [approversError, setApproversError] = useState(false)
  const [storeOptions, setStoreOptions] = useState<Item[]>([])
  const [areaOptions, setAreaOptions] = useState<Item[]>([])
  const [departmentOptions, setDepartmentOptions] = useState<Item[]>([])
  const [isLoadingFilters, setIsLoadingFilters] = useState(false)

  const [assignedFilters, setAssignedFilters] = useState<AssignedUserFilters>({})
  const [approverFilters, setApproverFilters] = useState<ApproverFilters>({})

  const loadAssignedUsers = useCallback(async (filters: AssignedUserFilters = {}) => {
    setIsLoadingAssigned(true)
    setAssignedError(false)
    try {
      const normalizedFilters = {
        ...filters,
        roles: filters.roles?.length ? filters.roles : [Roles.STORE, Roles.PARTNER],
      }
      const users = await getAvailableUsersForActionPlan(normalizedFilters)
      setAssignedUsers(users)
    } catch (error) {
      console.error('Error loading assigned users:', error)
      setAssignedError(true)
    } finally {
      setIsLoadingAssigned(false)
    }
  }, [])

  const loadApproverUsers = useCallback(async (filters: ApproverFilters = {}) => {
    setIsLoadingApprovers(true)
    setApproversError(false)
    try {
      const params = new URLSearchParams()
      if (filters.areas?.length) params.append('areas_ids', filters.areas.join(','))
      if (filters.departments?.length) params.append('departments_ids', filters.departments.join(','))
      if (filters.userTypes?.length) params.append('user_type_ids', filters.userTypes.join(','))

      const response = await actionPlansServices(httpFetch).getApprovers({
        queary: params.toString(),
      })

      if (response?.errors) {
        throw new Error('Error loading approvers')
      }

      const users = (Array.isArray(response) ? response : [])
        .filter((u): u is typeof u & { id: number } => typeof u.id === 'number')
        .map(mapToAvailableUser)
      setApproverUsers(users)
    } catch (error) {
      console.error('Error loading approver users:', error)
      setApproversError(true)
    } finally {
      setIsLoadingApprovers(false)
    }
  }, [])

  const loadFilterOptions = useCallback(async () => {
    setIsLoadingFilters(true)
    try {
      const [stores, areas, departments] = await Promise.all([
        getStoresForFilter(),
        areaService(httpFetch).getAllAreas(),
        departmentService(httpFetch).getAllDepartments(),
      ])

      const formattedStores = stores.map((store) => ({
        id: String(store.id),
        value: String(store.id),
        label: store.label,
      }))

      const formattedAreas = (Array.isArray(areas) ? areas : []).map((area) => ({
        id: String(area.id),
        value: String(area.id),
        label: area.name,
      }))

      const formattedDepartments = (Array.isArray(departments) ? departments : []).map(
        (department) => ({
          id: String(department.id),
          value: String(department.id),
          label: department.name,
          areaId: department.areaId,
        })
      )

      setStoreOptions(formattedStores)
      setAreaOptions(formattedAreas)
      setDepartmentOptions(formattedDepartments as Item[])
    } catch (error) {
      console.error('Error loading filter options:', error)
    } finally {
      setIsLoadingFilters(false)
    }
  }, [])

  useEffect(() => {
    loadAssignedUsers()
    loadApproverUsers()
    loadFilterOptions()
  }, [loadAssignedUsers, loadApproverUsers, loadFilterOptions])

  const assignedUserTypes = useMemo(() => {
    const types = new Map<number, Item>()
    assignedUsers.forEach((user) => {
      if (!types.has(user.userTypeId)) {
        types.set(user.userTypeId, {
          id: String(user.userTypeId),
          value: String(user.userTypeId),
          label: user.userType,
        })
      }
    })
    return Array.from(types.values())
  }, [assignedUsers])

  const assignedRoles = useMemo(() => {
    return listRoles
      .filter((role) => role.id === Roles.STORE || role.id === Roles.PARTNER)
      .map((role) => ({
        id: String(role.id),
        value: String(role.id),
        label: role.description,
      }))
  }, [])

  const filterOptions: FilterOptions = useMemo(
    () => ({
      stores: storeOptions,
      areas: areaOptions,
      departments: departmentOptions,
      assignedUserTypes,
      assignedRoles,
    }),
    [storeOptions, areaOptions, departmentOptions, assignedUserTypes, assignedRoles]
  )

  const applyAssignedFilters = useCallback(
    (filters: AssignedUserFilters) => {
      setAssignedFilters(filters)
      loadAssignedUsers(filters)
    },
    [loadAssignedUsers]
  )

  const applyApproverFilters = useCallback(
    (filters: ApproverFilters) => {
      setApproverFilters(filters)
      loadApproverUsers(filters)
    },
    [loadApproverUsers]
  )

  return {
    assignedUsers,
    approverUsers,
    isLoadingAssigned,
    isLoadingApprovers,
    assignedError,
    approversError,
    filterOptions,
    isLoadingFilters,
    assignedFilters,
    approverFilters,
    applyAssignedFilters,
    applyApproverFilters,
    refreshAssignedUsers: loadAssignedUsers,
    refreshApproverUsers: loadApproverUsers,
  }
}
